import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useClickOutside } from 'services/useClickOutside';

const PANEL_MAX_H = 220;

const getPortalStyle = (buttonEl) => {
  if (!buttonEl) return {};
  const rect = buttonEl.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  const openAbove = spaceBelow < PANEL_MAX_H && rect.top > spaceBelow;
  const left = Math.min(rect.left, window.innerWidth - rect.width - 8);
  return {
    position: 'fixed',
    top: openAbove ? `${rect.top - PANEL_MAX_H - 8}px` : `${rect.bottom + 4}px`,
    left: `${Math.max(8, left)}px`,
    width: `${rect.width}px`,
    maxHeight: `${PANEL_MAX_H}px`,
    zIndex: 70,
  };
};

/**
 * Plain light dropdown over a fixed/known list of plain-text options — no color
 * mapping, no free text. Use for filters over values that already exist in the
 * data (e.g. Health `area` filter). For selection with a color/tag rendering
 * per option, use a dedicated component instead (e.g. health/DropdownType).
 * For free text with suggestions, use DropdownWithInput.
 * Same visual language/portal behavior as DropdownCountrySelect and DropdownType.
 */
const DropdownSelect = ({
  value = '',
  onChange,
  options = [],
  placeholder = 'Select',
  showAllOption = false,
  allOptionText = 'All',
  disabled = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  useClickOutside([buttonRef, panelRef], () => setIsOpen(false), isOpen);

  const handleSelect = (val) => {
    onChange(val);
    setIsOpen(false);
  };

  const displayLabel = value || (showAllOption ? allOptionText : placeholder);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setIsOpen((o) => !o);
        }}
        className={`flex items-center justify-between gap-2 w-full rounded-lg px-2 py-1.5 text-left bg-gray-100 hover:bg-gray-200 border-0 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-gray-400 ${className}`}
      >
        <span className={`truncate text-xs ${value ? 'text-gray-900' : 'text-gray-400'}`}>{displayLabel}</span>
        <ChevronDownIcon className={`h-4 w-4 text-gray-500 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={panelRef}
            style={getPortalStyle(buttonRef.current)}
            className="bg-white rounded-lg shadow-xl border border-gray-200 overflow-y-auto p-1"
            onMouseDown={(e) => e.stopPropagation()}
          >
            {showAllOption && (
              <button
                type="button"
                onClick={() => handleSelect('')}
                className={`w-full flex items-center px-2 py-1.5 rounded text-left text-xs hover:bg-gray-100 ${value === '' ? 'bg-gray-100' : ''}`}
              >
                <span className="text-gray-500">{allOptionText}</span>
              </button>
            )}
            {options.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => handleSelect(option)}
                className={`w-full flex items-center px-2 py-1.5 rounded text-left text-xs hover:bg-gray-100 ${
                  value === option ? 'bg-gray-100 font-medium text-gray-900' : 'text-gray-700'
                }`}
              >
                {option}
              </button>
            ))}
            {options.length === 0 && (
              <div className="px-2 py-1.5 text-xs text-gray-400 italic">No options available</div>
            )}
          </div>,
          document.body
        )}
    </>
  );
};

export default DropdownSelect;
